import { api } from '@/lib/api';

export interface Account {
  id: string; name: string; type: string;
  bankCode?: string | null; agency?: string | null; accountNumber?: string | null;
  initialBalance: string; balance: string; active: boolean;
}

export interface ChartOfAccount {
  id: string; code: string; name: string; nature: 'REVENUE' | 'EXPENSE' | 'ASSET' | 'LIABILITY';
  parentId: string | null; active: boolean;
  /** true para RECEITAS, DESPESAS e Outros (sentinelas — não editáveis nem excluíveis pela UI) */
  isSystem?: boolean;
}

export interface ChartTreeNode extends ChartOfAccount { children: ChartTreeNode[]; }

export interface ChartUsage {
  transactions: number;
  payables: number;
  receivables: number;
  recurringRules: number;
  total: number;
}

export interface CostCenter { id: string; code: string; name: string; active: boolean; }

export interface Party {
  id: string; code: string; name: string; type: 'CUSTOMER' | 'SUPPLIER' | 'EMPLOYEE' | 'CONTRACTOR';
  email?: string | null; phone?: string | null; document?: string | null; notes?: string | null; active: boolean;
}

export type Direction = 'IN' | 'OUT';
export type TxStatus = 'CONFIRMED' | 'PENDING' | 'RECONCILED';

export interface Transaction {
  id: string; direction: Direction; amount: string; date: string; description: string;
  reference?: string | null; paymentMethod: string; status: TxStatus;
  reversedById: string | null;
  reversedBy: { id: string }[];
  account: { id: string; name: string };
  chartOfAccount: { id: string; code: string; name: string };
  costCenter?: { id: string; name: string } | null;
  party?: { id: string; code: string; name: string } | null;
  createdBy: { id: string; name: string };
  /** Quando preenchido, é uma transação de transferência entre caixas (não conta nos somatórios). */
  transferId?: string | null;
  transfer?: {
    id: string;
    fromAccount: { id: string; name: string };
    toAccount: { id: string; name: string };
  } | null;
}

export interface Transfer {
  id: string;
  amount: string;
  date: string;
  description: string | null;
  fromAccount: { id: string; name: string };
  toAccount: { id: string; name: string };
  createdBy: { id: string; name: string };
  createdAt: string;
  updatedAt: string;
}

export interface Paged<T> { items: T[]; total: number; skip: number; take: number; }

export type PayableStatus = 'OPEN' | 'PARTIAL' | 'PAID' | 'OVERDUE' | 'CANCELED';
export type ReceivableStatus = 'OPEN' | 'PARTIAL' | 'RECEIVED' | 'OVERDUE' | 'CANCELED';

export interface Payable {
  id: string; description: string; reference?: string | null;
  amount: string; paidAmount: string; dueDate: string; issueDate: string; status: PayableStatus;
  installmentNumber: number | null; installmentTotal: number | null;
  party: { id: string; code: string; name: string };
  chartOfAccount: { id: string; code: string; name: string };
  costCenter?: { id: string; name: string } | null;
  notes?: string | null;
}

export interface Receivable {
  id: string; description: string; reference?: string | null;
  amount: string; receivedAmount: string; dueDate: string; issueDate: string; status: ReceivableStatus;
  installmentNumber: number | null; installmentTotal: number | null;
  party: { id: string; code: string; name: string };
  chartOfAccount: { id: string; code: string; name: string };
  costCenter?: { id: string; name: string } | null;
  notes?: string | null;
}

export type RecurringStatus = 'ACTIVE' | 'WAITING' | 'PAUSED' | 'CANCELED';
export interface RecurringRule {
  id: string; description: string; direction: Direction;
  amount: string; dayOfMonth: number; startDate: string; endDate: string | null;
  status: RecurringStatus; lastGeneratedAt: string | null;
  party: { id: string; code: string; name: string };
  chartOfAccount: { id: string; code: string; name: string };
  costCenter?: { id: string; name: string } | null;
}

export interface GenerateNextResult {
  created: number;
  skipped: number;
  details: Array<{
    ruleId: string;
    description: string;
    targetMonth: string;
    status: 'created' | 'skipped';
    reason?: string;
    generatedId?: string;
  }>;
}

export interface GenerateOneResult {
  created: boolean;
  reason?: string;
  targetMonth: string;
  generatedId?: string;
}

// =====================  helpers genéricos =====================
function qs(params: Record<string, string | number | boolean | undefined>) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// =====================  Accounts  =====================
export const accountsApi = {
  list: () => api<Account[]>('/accounts'),
  create: (b: Partial<Account>) => api<Account>('/accounts', { method: 'POST', body: b }),
  update: (id: string, b: Partial<Account>) => api<Account>(`/accounts/${id}`, { method: 'PATCH', body: b }),
};

// =====================  Chart of accounts  =====================
export const chartApi = {
  list: () => api<ChartOfAccount[]>('/chart-of-accounts'),
  tree: () => api<ChartTreeNode[]>('/chart-of-accounts?tree=true'),
  create: (b: Partial<ChartOfAccount>) => api<ChartOfAccount>('/chart-of-accounts', { method: 'POST', body: b }),
  update: (id: string, b: Partial<ChartOfAccount>) =>
    api<ChartOfAccount>(`/chart-of-accounts/${id}`, { method: 'PATCH', body: b }),
  usage: (id: string) => api<ChartUsage>(`/chart-of-accounts/${id}/usage`),
  remove: (id: string, reassign = false) =>
    api<{ deleted: true; reassignedTo: string | null; usage: ChartUsage }>(
      `/chart-of-accounts/${id}${reassign ? '?reassign=true' : ''}`,
      { method: 'DELETE' },
    ),
};

// =====================  Cost centers  =====================
export const costCentersApi = {
  list: () => api<CostCenter[]>('/cost-centers'),
  create: (b: Partial<CostCenter>) => api<CostCenter>('/cost-centers', { method: 'POST', body: b }),
  update: (id: string, b: Partial<CostCenter>) => api<CostCenter>(`/cost-centers/${id}`, { method: 'PATCH', body: b }),
};

// =====================  Parties  =====================
export interface PartyUsage {
  transactions: number;
  payables: number;
  receivables: number;
  recurringRules: number;
  total: number;
}

export const partiesApi = {
  list: (params?: { type?: string; q?: string }) => api<Party[]>(`/parties${qs(params ?? {})}`),
  create: (b: Partial<Party>) => api<Party>('/parties', { method: 'POST', body: b }),
  update: (id: string, b: Partial<Party>) => api<Party>(`/parties/${id}`, { method: 'PATCH', body: b }),
  usage: (id: string) => api<PartyUsage>(`/parties/${id}/usage`),
  remove: (id: string) =>
    api<{ deleted: true; id: string }>(`/parties/${id}`, { method: 'DELETE' }),
};

export interface TransactionsSummary {
  period: {
    paid: { count: number; amount: string };
    received: { count: number; amount: string };
    monthBalance: string;
  };
  filtered: {
    inAmount: string;
    outAmount: string;
    netAmount: string;
    inCount: number;
    outCount: number;
    count: number;
  };
}

// =====================  Transfers  =====================
export const transfersApi = {
  list: (params: Record<string, string | number | boolean | undefined> = {}) =>
    api<Paged<Transfer>>(`/transfers${qs(params)}`),
  get: (id: string) => api<Transfer>(`/transfers/${id}`),
  create: (b: {
    fromAccountId: string;
    toAccountId: string;
    amount: number;
    date: string;
    description?: string;
  }) => api<Transfer>('/transfers', { method: 'POST', body: b }),
  update: (
    id: string,
    b: {
      fromAccountId?: string;
      toAccountId?: string;
      amount?: number;
      date?: string;
      description?: string;
    },
  ) => api<Transfer>(`/transfers/${id}`, { method: 'PATCH', body: b }),
  remove: (id: string) =>
    api<{ id: string; deleted: true }>(`/transfers/${id}`, { method: 'DELETE' }),
};

// =====================  Transactions  =====================
export const transactionsApi = {
  list: (params: Record<string, string | number | boolean | undefined> = {}) =>
    api<Paged<Transaction>>(`/transactions${qs(params)}`),
  summary: (params: Record<string, string | number | boolean | undefined> = {}) =>
    api<TransactionsSummary>(`/transactions/summary${qs(params)}`),
  create: (b: {
    accountId: string; chartOfAccountId: string; costCenterId?: string; partyId?: string;
    direction: Direction; amount: number; date: string; description: string; reference?: string;
    paymentMethod?: string;
  }) => api<Transaction>('/transactions', { method: 'POST', body: b }),
  reverse: (id: string, motivo: string) =>
    api<Transaction>(`/transactions/${id}/reverse`, { method: 'POST', body: { motivo } }),
  reconcile: (id: string) => api<Transaction>(`/transactions/${id}/reconcile`, { method: 'POST' }),
};

export interface PayablesSummary {
  overdue: { count: number; amount: string };
  dueToday: { count: number; amount: string };
  upcoming: { count: number; amount: string };
  paid: { count: number; amount: string };
  total: { count: number; amount: string };
}

// =====================  Payables  =====================
export const payablesApi = {
  list: (params: Record<string, string | undefined> = {}) =>
    api<Paged<Payable>>(`/payables${qs(params)}`),
  summary: (params: Record<string, string | undefined> = {}) =>
    api<PayablesSummary>(`/payables/summary${qs(params)}`),
  get: (id: string) => api<Payable>(`/payables/${id}`),
  create: (b: Record<string, unknown>) => api<Payable>('/payables', { method: 'POST', body: b }),
  installments: (b: Record<string, unknown>) =>
    api<Payable>('/payables/installments', { method: 'POST', body: b }),
  pay: (id: string, b: Record<string, unknown>) =>
    api<{ transaction: Transaction; payable: Payable }>(`/payables/${id}/pay`, { method: 'POST', body: b }),
  update: (id: string, b: Record<string, unknown>) =>
    api<Payable>(`/payables/${id}`, { method: 'PATCH', body: b }),
  cancel: (id: string) => api<Payable>(`/payables/${id}/cancel`, { method: 'PATCH' }),
  remove: (id: string) =>
    api<{ id: string; deleted: true }>(`/payables/${id}`, { method: 'DELETE' }),
};

export interface ReceivablesSummary {
  overdue: { count: number; amount: string };
  dueToday: { count: number; amount: string };
  upcoming: { count: number; amount: string };
  received: { count: number; amount: string };
  total: { count: number; amount: string };
}

// =====================  Receivables  =====================
export const receivablesApi = {
  list: (params: Record<string, string | undefined> = {}) =>
    api<Paged<Receivable>>(`/receivables${qs(params)}`),
  summary: (params: Record<string, string | undefined> = {}) =>
    api<ReceivablesSummary>(`/receivables/summary${qs(params)}`),
  get: (id: string) => api<Receivable>(`/receivables/${id}`),
  create: (b: Record<string, unknown>) => api<Receivable>('/receivables', { method: 'POST', body: b }),
  installments: (b: Record<string, unknown>) =>
    api<Receivable>('/receivables/installments', { method: 'POST', body: b }),
  receive: (id: string, b: Record<string, unknown>) =>
    api<{ transaction: Transaction; receivable: Receivable }>(`/receivables/${id}/receive`, { method: 'POST', body: b }),
  update: (id: string, b: Record<string, unknown>) =>
    api<Receivable>(`/receivables/${id}`, { method: 'PATCH', body: b }),
  cancel: (id: string) => api<Receivable>(`/receivables/${id}/cancel`, { method: 'PATCH' }),
  remove: (id: string) =>
    api<{ id: string; deleted: true }>(`/receivables/${id}`, { method: 'DELETE' }),
};

// =====================  Recurring  =====================
export const recurringApi = {
  list: (params: Record<string, string | undefined> = {}) =>
    api<RecurringRule[]>(`/recurring-rules${qs(params)}`),
  get: (id: string) => api<RecurringRule>(`/recurring-rules/${id}`),
  create: (b: Record<string, unknown>) => api<RecurringRule>('/recurring-rules', { method: 'POST', body: b }),
  update: (id: string, b: Record<string, unknown>) =>
    api<RecurringRule>(`/recurring-rules/${id}`, { method: 'PATCH', body: b }),
  remove: (id: string) =>
    api<{ id: string; deleted: true }>(`/recurring-rules/${id}`, { method: 'DELETE' }),
  /** Gera o próximo título pendente para todas as regras ativas. */
  generateNext: () =>
    api<GenerateNextResult>('/recurring-rules/generate-next', { method: 'POST' }),
  /** Gera o próximo título pendente para UMA regra específica. */
  generateOne: (id: string) =>
    api<GenerateOneResult>(`/recurring-rules/${id}/generate-next`, { method: 'POST' }),
  /** @deprecated Use `generateNext()`. */
  generateNow: () =>
    api<GenerateNextResult>('/recurring-rules/generate-now', { method: 'POST' }),
};

// =====================  Reports  =====================
export interface DashboardData {
  consolidatedBalance: string;
  accounts: { id: string; name: string; balance: string }[];
  /** Totais de Payable em aberto/parcial dentro do período filtrado. */
  payablePeriod: { count: number; total: string };
  /** Totais de Receivable em aberto/parcial dentro do período filtrado. */
  receivablePeriod: { count: number; total: string };
  /** Totais já efetivados (Transactions confirmadas) com `date` no período. */
  paidPeriod?: { total: string };
  receivedPeriod?: { total: string };
  /** @deprecated alias retrocompatível — usar `payablePeriod`. */
  payable30: { count: number; total: string };
  /** @deprecated alias retrocompatível — usar `receivablePeriod`. */
  receivable30: { count: number; total: string };
  overduePayables: { count: number; total: string };
  overdueReceivables: { count: number; total: string };
  daily: { date: string; in: string; out: string }[];
  upcoming: { payables: Payable[]; receivables: Receivable[] };
  /** Listagem de Payables/Receivables com status OVERDUE. Pode estar ausente em backends antigos. */
  overdue?: { payables: Payable[]; receivables: Receivable[] };
}

export const reportsApi = {
  dashboard: (params: { from?: string; to?: string } = {}) =>
    api<DashboardData>(`/reports/dashboard${qs(params)}`),
  cashFlow: (from: string, to: string) =>
    api<{
      series: { date: string; in: number; out: number; balance: number }[];
      totalIn: number;
      totalOut: number;
      initialBalance: number;
      finalBalance: number;
    }>(`/reports/cash-flow${qs({ from, to })}`),
  dre: (from: string, to: string) =>
    api<{
      revenue: string;
      expense: string;
      result: string;
      breakdown: { chartOfAccount?: ChartOfAccount; direction: Direction; total: string }[];
    }>(`/reports/dre${qs({ from, to })}`),
  byCategory: (from: string, to: string) =>
    api<{ chartOfAccount?: ChartOfAccount; direction: Direction; total: string }[]>(`/reports/by-category${qs({ from, to })}`),
  aging: (type: 'payable' | 'receivable') =>
    api<{ label: string; count: number; total: string; items: unknown[] }[]>(`/reports/aging${qs({ type })}`),
  projection: (year: number, includeRecurring = false) =>
    api<ProjectionData>(
      `/reports/projection${qs({ year: String(year), includeRecurring: includeRecurring ? 'true' : '' })}`,
    ),
};

export interface ProjectionMonth {
  month: number;
  label: string;
  received: string;
  paid: string;
  flow: string;
  endingBalance: string;
}

export interface ProjectionAggregate {
  label: string;
  received: string;
  paid: string;
  flow: string;
  endingBalance: string;
}

export interface ProjectionData {
  year: number;
  initialBalance: string;
  months: ProjectionMonth[];
  quarters: ProjectionAggregate[];
  semesters: ProjectionAggregate[];
  yearTotal: ProjectionAggregate;
}
